/**
 * Image Block for Editor.js
 * Creates email-safe image elements with Cloudinary integration
 */

import { BlockTool } from '@editorjs/editorjs';

export interface ImageBlockData {
  url: string;
  alt: string;
  caption: string;
  alignment: 'left' | 'center' | 'right';
  width: 'full' | 'medium' | 'small';
}

export default class ImageBlock implements BlockTool {
  private data: ImageBlockData;
  private wrapper: HTMLElement | null = null;

  static get toolbox() {
    return {
      title: 'Image',
      icon: '<svg width="17" height="15" viewBox="0 0 17 15" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="15" height="13" rx="1" stroke="currentColor" stroke-width="1.5" fill="none"/><circle cx="5" cy="5" r="1.5" fill="currentColor"/><path d="M1 11l4-4 3 3 4-5 4 5" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>',
    };
  }

  constructor({ data }: { data: ImageBlockData }) {
    this.data = {
      url: data.url || '',
      alt: data.alt || '',
      caption: data.caption || '',
      alignment: data.alignment || 'center',
      width: data.width || 'full',
    };
  }

  render(): HTMLElement {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('email-image-block');
    this.updateWrapper();
    return this.wrapper;
  }

  private getWidthValue(): string {
    const widths: Record<string, string> = {
      full: '100%',
      medium: '75%',
      small: '50%',
    };
    return widths[this.data.width] || '100%';
  }

  private updateWrapper(): void {
    if (!this.wrapper) return;

    this.wrapper.style.cssText = `
      text-align: ${this.data.alignment};
      padding: 16px 0;
    `;

    if (!this.data.url) {
      // Show upload placeholder
      this.wrapper.innerHTML = `
        <div style="
          border: 2px dashed #ddd;
          border-radius: 8px;
          padding: 40px;
          cursor: pointer;
          background: #fafafa;
        ">
          <p style="margin: 0; color: #666; font-size: 14px;">
            Click to upload an image
          </p>
        </div>
      `;
      this.wrapper.querySelector('div')?.addEventListener('click', () => this.openImageUpload());
    } else {
      const imgContainer = document.createElement('div');
      imgContainer.style.cssText = `display: inline-block; width: ${this.getWidthValue()};`;

      const img = document.createElement('img');
      img.src = this.data.url;
      img.alt = this.data.alt;
      img.style.cssText = `
        width: 100%;
        height: auto;
        border-radius: 4px;
        cursor: pointer;
      `;
      img.addEventListener('click', () => this.openImageUpload());

      imgContainer.appendChild(img);

      if (this.data.caption) {
        const caption = document.createElement('p');
        caption.contentEditable = 'true';
        caption.textContent = this.data.caption;
        caption.style.cssText = `
          font-size: 12px;
          color: #666;
          margin: 8px 0 0;
          font-style: italic;
          outline: none;
        `;
        caption.addEventListener('input', () => {
          this.data.caption = caption.textContent || '';
        });
        imgContainer.appendChild(caption);
      }

      this.wrapper.innerHTML = '';
      this.wrapper.appendChild(imgContainer);
    }
  }

  private openImageUpload(): void {
    // @ts-ignore - Cloudinary widget
    if (!window.cloudinary) {
      const url = prompt('Enter image URL:', this.data.url);
      if (url) {
        this.data.url = url;
        this.data.alt = 'Email image';
        this.updateWrapper();
      }
      return;
    }

    // @ts-ignore
    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: 'dcyi0j3ky',
        uploadPreset: 'email_images',
        sources: ['local', 'url', 'camera'],
        multiple: false,
        resourceType: 'image',
        clientAllowedFormats: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
        maxImageFileSize: 5000000,
        maxImageWidth: 1200,
        folder: 'email-campaigns',
      },
      (error: any, result: any) => {
        if (!error && result && result.event === 'success') {
          this.data.url = result.info.secure_url;
          this.data.alt = result.info.original_filename || 'Email image';
          this.updateWrapper();
          widget.close();
        }
      }
    );
    widget.open();
  }

  save(): ImageBlockData {
    return this.data;
  }

  renderSettings(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.padding = '8px';

    // Alt text
    const altLabel = document.createElement('label');
    altLabel.textContent = 'Alt Text:';
    altLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const altInput = document.createElement('input');
    altInput.type = 'text';
    altInput.value = this.data.alt;
    altInput.placeholder = 'Describe the image...';
    altInput.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 12px;';
    altInput.addEventListener('change', () => {
      this.data.alt = altInput.value;
    });

    // Caption
    const captionLabel = document.createElement('label');
    captionLabel.textContent = 'Caption (optional):';
    captionLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const captionInput = document.createElement('input');
    captionInput.type = 'text';
    captionInput.value = this.data.caption;
    captionInput.placeholder = 'Add a caption...';
    captionInput.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 12px;';
    captionInput.addEventListener('change', () => {
      this.data.caption = captionInput.value;
      this.updateWrapper();
    });

    // Width selector
    const widthLabel = document.createElement('label');
    widthLabel.textContent = 'Width:';
    widthLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const widthSelect = document.createElement('select');
    widthSelect.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 12px;';

    [
      { value: 'full', label: 'Full Width' },
      { value: 'medium', label: 'Medium (75%)' },
      { value: 'small', label: 'Small (50%)' },
    ].forEach(({ value, label }) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      option.selected = this.data.width === value;
      widthSelect.appendChild(option);
    });

    widthSelect.addEventListener('change', () => {
      this.data.width = widthSelect.value as ImageBlockData['width'];
      this.updateWrapper();
    });

    // Alignment selector
    const alignLabel = document.createElement('label');
    alignLabel.textContent = 'Alignment:';
    alignLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const alignSelect = document.createElement('select');
    alignSelect.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;';

    ['left', 'center', 'right'].forEach((align) => {
      const option = document.createElement('option');
      option.value = align;
      option.textContent = align.charAt(0).toUpperCase() + align.slice(1);
      option.selected = this.data.alignment === align;
      alignSelect.appendChild(option);
    });

    alignSelect.addEventListener('change', () => {
      this.data.alignment = alignSelect.value as ImageBlockData['alignment'];
      this.updateWrapper();
    });

    wrapper.appendChild(altLabel);
    wrapper.appendChild(altInput);
    wrapper.appendChild(captionLabel);
    wrapper.appendChild(captionInput);
    wrapper.appendChild(widthLabel);
    wrapper.appendChild(widthSelect);
    wrapper.appendChild(alignLabel);
    wrapper.appendChild(alignSelect);

    return wrapper;
  }
}
